import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import SearchIcon from '@mui/icons-material/Search';
import {
  TableRow,
  TableCell
} from '@mui/material';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import { ClearButton } from '../../../components/Button/iconbutton';
import ChartButton from '../../../components/Button/ChartButton';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import Chart from '../../../components/Chart'
import { Creatinine, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const CreatinineForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [creatinineRecords, setCreatinineRecords] = useState<Creatinine[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showChart, setShowChart] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  })
  const [snackbarSeverity, setSnackbarSeverity] = useState<
  'success' | 'info' | 'warning' | 'error'
>('success')
  const [showClearFilterButton, setShowClearFilterButton] = useState(false)
  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }


  useEffect(() => {
    fetchCreatinineRecords();
    setShowClearFilterButton(
      !!(searchTerm !== '' || (dateRange.startDate && dateRange.endDate))
    );
  }, [page, rowsPerPage, searchTerm, dateRange]);

  const fetchCreatinineRecords = async (startDate: string = '', endDate: string = '') => {
    setLoading(true);
    const authToken = sessionStorage.getItem('authToken');
    try {
      const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
        params: {
          limit: rowsPerPage,
          page: page + 1,
          from: startDate ,
          to: endDate ,
          order_by: 'details->date',
          dir: 2, 
          user_id: patientId,
          slug: 'creatinine',
          searchkey: searchTerm || undefined,
        },
        headers: getAxiosConfig().headers,
      });

      const vitalsData = response.data.data.vitals;
      const total = response.data.data.pagination!.total;

      if (Array.isArray(vitalsData) && vitalsData.length > 0) {
        const formattedData: Creatinine[] = vitalsData.map((vital) => ({
          id: vital.id,
          user_id: vital.user_id, // Ensure user_id is included
          consult_id: vital.consult_id ?? null, // Allow null
          peripheral_id: vital.peripheral_id ?? null, // Allow null
          slug: 'creatinine', // Default to empty string if not available
          details: {
            date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
            time: vital.details.time,
            unit: vital.details.unit ?? 'mg/dL', // Default unit
            creatinine: vital.details.creatinine,
            range_code: vital.details.range_code,
            creatinineFlag: vital.details.creatinineFlag,
            creatinineFlagColor: vital.details.creatinineFlagColor,
          },
          freeze: vital.freeze, // Ensure this is included
          document: vital.document ?? null, // Allow null
        }));
        console.log('Formatted Data:', formattedData); // Debug formatted data
        setCreatinineRecords(formattedData);
        setTotalRecords(total ?? 0);
      } else {
        console.warn('No vitals data received.'); // Warn if no data
        setCreatinineRecords([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching blood ketone records:', error);
      showSnackbar('Failed to fetch blood ketone records. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setPage(0); // Reset to the first page
    setRowsPerPage(5); // Reset rows per page if needed
    fetchCreatinineRecords(); // Fetch all records again
    setShowClearFilterButton(false); // Hide the clear button
  };


  const renderRow = (item: Creatinine, index: number) => (
    <TableRow key={index}>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>{page * rowsPerPage + index + 1}</TableCell>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>{(item.details.date)}</TableCell>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>{item.details.time}</TableCell>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>{item.details.creatinine} mg/dL</TableCell>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'}}>
        <span
          style={{
            backgroundColor: item.details.range_code,
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#FFFFFF',
          }}
        >
          {item.details.creatinineFlag}
        </span>
      </TableCell>
    </TableRow>
  );

  // Correct data mapping for the chart
  const chartData = {
    labels: creatinineRecords.map((record) => record.details.date),
    datasets: [
      {
        label: 'Creatinine Level (mg/dL)',
        data: creatinineRecords.map((record) =>
          typeof record.details.creatinine === 'number' ? record.details.creatinine : 0
        ), // Ensure that only numbers are passed
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1,
      },
    ],
  };


  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) =>
            `${context.dataset.label}: ${context.raw} mg/dL`,
        },
      },
    },
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between w-full gap-4 pb-4">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId={'Creatinine_Start_Date'}
            endDateId={'Creatinine_End_Date'}
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates);
            }}
            value={dateRange}
          />
          
          <IconButton
            icon={<SearchIcon />}
            onClick={() =>
              fetchCreatinineRecords(dateRange.startDate, dateRange.endDate)
            }
          />
          {showClearFilterButton && (
            <ClearButton onClick={handleClearFilters} />
          )}
        </div>
        <div className="flex justify-end space-x-4">

        {creatinineRecords.length > 0 && (
                    <ChartButton text={showChart ? 'List' : 'Chart'} onClick={() => setShowChart(prev => !prev)} />
                )}
         </div>
      </div>


      {loading ? (

<div className="text-center my-4">
 
</div>
) : (
<>
  {showChart ? (
    <Chart data={chartData} options={chartOptions} />
  ) : (
    <div>
              <TableComponent
                headers={[
                  'S.No',
                  'Date',
                  'Time',
                  'Value (mg/dL)',
                  'Result'    
                ]}
                data={creatinineRecords}
                loading={loading}
                renderRow={renderRow} error={''} />
                
                </div>
          )}
        </>
      )}

              <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
         
    </div>
  );
};

export default CreatinineForm;
